<script>
import Multiselect from "vue-multiselect";
import { errorMessages } from "@/helpers/validations/errorMessages";

/**
 * ValidatedMultiselect Component
 * Wrapper component for vue-multiselect with vuelidate validation support
 * 
 * Props:
 * - value: The v-model value (required)
 * - options: Array of options to display (required)
 * - validation: Vuelidate validation object (required)
 * - All other props are passed directly to multiselect component
 * 
 * Usage:
 * <validated-multiselect
 *   v-model="form.field"
 *   :options="options"
 *   :validation="$v.form.field"
 *   :multiple="true"
 *   label="name"
 *   track-by="id"
 *   placeholder="Select..."
 * />
 */
export default {
  name: "ValidatedMultiselect",
  components: {
    Multiselect,
  },
  props: {
    value: {
      required: true,
    },
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    validation: {
      type: Object,
      required: true,
    },
    // Common multiselect props
    multiple: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "label",
    },
    trackBy: {
      type: String,
      default: "id",
    },
    placeholder: {
      type: String,
      default: "Select...",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    allowEmpty: {
      type: Boolean,
      default: true,
    },
    maxHeight: {
      type: Number,
      default: 300,
    },
    // Pass through any other props
  },
  computed: {
    hasError() {
      return this.validation && this.validation.$error;
    },
    errorMessage() {
      if (!this.hasError) return "";
      
      if (this.validation.$params && this.validation.$params.required && !this.validation.required) {
        return errorMessages.required;
      }
      
      // Add more error message conditions as needed
      return "";
    },
  },
  methods: {
    handleInput(value) {
      this.$emit("input", value);
      // Touch validation on change
      if (this.validation && this.validation.$touch) {
        this.validation.$touch();
      }
    },
  },
};
</script>

<template>
  <div>
    <div
      class="validated-multiselect-wrapper"
      :class="{
        'is-invalid': hasError,
      }"
    >
      <multiselect
        :value="value"
        @input="handleInput"
        :options="options"
        :multiple="multiple"
        :label="label"
        :track-by="trackBy"
        :placeholder="placeholder"
        :disabled="disabled"
        :searchable="searchable"
        :allow-empty="allowEmpty"
        :max-height="maxHeight"
        :class="{
          'multiselect--invalid': hasError,
        }"
        v-bind="$attrs"
        v-on="$listeners"
      ></multiselect>
    </div>
    <div v-if="hasError && errorMessage" class="invalid-feedback">
      {{ errorMessage }}
    </div>
  </div>
</template>

<style>
/* Styles for multiselect validation errors */
.validated-multiselect-wrapper.is-invalid >>> .multiselect .multiselect__tags {
  border-color: #dc3545 !important;
  border-width: 1px;
}

.validated-multiselect-wrapper.is-invalid >>> .multiselect .multiselect__tags:focus-within {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.validated-multiselect-wrapper >>> .multiselect--invalid .multiselect__tags {
  border-color: #dc3545 !important;
  border-width: 1px;
}

.validated-multiselect-wrapper >>> .multiselect--invalid .multiselect__tags:focus-within {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}
</style>
